<template>
  <div class="condition-group" :class="{'is-nested': level > 0, 'is-compact': compact}">
    <div class="group-header">
      <div class="operator-selector">
        <span class="operator-label">{{ t('forms-conditions-operator_label') }}</span>
        <kv-select
          class="operator-select"
          variant="outlined"
          size="small"
          full-width
          :options="groupOperatorOptions"
          :value="localGroup.conditional_operator"
          @input="setGroupOperator"
        />
      </div>

      <div class="group-actions">
        <kv-button variant="outlined" size="small" prepend-icon="ui-plus" @click="addCondition">
          {{ t('forms-conditions-btn_add_condition') }}
        </kv-button>
        <kv-button variant="outlined" size="small" prepend-icon="ui-plus" @click="addGroup">
          {{ t('forms-conditions-btn_add_group') }}
        </kv-button>
        <kv-button
          v-if="level > 0"
          variant="text"
          size="small"
          prepend-icon="ui-trash-01"
          :aria-label="t('forms-conditions-remove_group')"
          @click="$emit('remove')"
        />
      </div>
    </div>

    <div class="group-body">
      <div v-if="isEmpty" class="empty-group">
        <Icon icon-class="ui-alert-circle" size="3" />
        <span>{{ t('forms-conditions-empty_message') }}</span>
      </div>

      <div v-else class="conditions-list">
        <div
          v-for="(condition, index) in localGroup.conditions"
          :key="`condition-${index}`"
          class="condition-item"
        >
          <div class="condition-card">
            <div class="condition-card-header">
              <span class="condition-label">{{ t('forms-conditions-condition_label') }}</span>
              <kv-button
                variant="text"
                size="small"
                prepend-icon="ui-trash-01"
                :aria-label="t('forms-conditions-remove_condition')"
                @click="removeCondition(index)"
              />
            </div>

            <div class="condition-card-body">
              <div class="condition-field">
                <span class="field-label">{{ t('forms-conditions-question_label') }}</span>
                <kv-select
                  variant="outlined"
                  size="small"
                  full-width
                  :options="questionOptions"
                  :placeholder="t('forms-conditions-select_placeholder')"
                  :value="condition.question_id"
                  @input="(value) => setQuestion(condition, value)"
                />
              </div>

              <div class="condition-field">
                <span class="field-label">{{ t('forms-conditions-operator_select_label') }}</span>
                <kv-select
                  variant="outlined"
                  size="small"
                  full-width
                  :options="getAvailableOperators(condition.question_id)"
                  :placeholder="t('forms-conditions-select_placeholder')"
                  :value="condition.comparison_operator"
                  @input="(value) => setOperator(condition, value)"
                />
              </div>

              <div
                class="condition-field"
                v-if="condition.comparison_operator !== 'QUESTION_ANSWERED'"
              >
                <span class="field-label">{{ t('forms-conditions-value_label') }}</span>

                <kv-select
                  v-if="valueOptions(condition).length > 0"
                  variant="outlined"
                  size="small"
                  full-width
                  :options="valueOptions(condition)"
                  :placeholder="t('forms-conditions-select_placeholder')"
                  :value="matchedValue(condition)"
                  @input="(value) => setValue(condition, value)"
                />

                <kv-date-picker
                  v-else-if="getQuestionType(condition.question_id) === 'date'"
                  variant="outlined"
                  size="small"
                  value-format="yyyy-MM-dd"
                  :placeholder="t('forms-conditions-select_placeholder')"
                  :value="condition.condition_value"
                  @input="(value) => setValue(condition, value)"
                />

                <kv-input
                  v-else
                  variant="outlined"
                  size="small"
                  :type="requiresNumericInput(condition.comparison_operator) ? 'number' : 'text'"
                  :placeholder="t('forms-conditions-value_placeholder')"
                  :value="condition.condition_value"
                  @input="(value) => setValue(condition, value)"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-for="(childGroup, index) in localGroup.childrens"
          :key="`group-${index}`"
          class="nested-group-item"
        >
          <condition-group
            :group="childGroup"
            :level="level + 1"
            :available-questions="availableQuestions"
            :compact="compact"
            @change="(updatedGroup) => updateChildGroup(index, updatedGroup)"
            @remove="removeChildGroup(index)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from "../../components/icon/icon";
import KvButton from "../../components/form/kv-button.vue";
import KvInput from "../../components/inputs/kv-input.vue";
import KvSelect from "../../components/inputs/kv-select.vue";
import KvDatePicker from "../../components/inputs/kv-date-picker.vue";

export default {
  name: "ConditionGroup",
  components: {
    Icon,
    "kv-button": KvButton,
    "kv-input": KvInput,
    "kv-select": KvSelect,
    "kv-date-picker": KvDatePicker,
  },
  props: {
    group: {
      type: Object,
      required: true
    },
    level: {
      type: Number,
      default: 0
    },
    availableQuestions: {
      type: Array,
      default: () => []
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localGroup: this.group
    };
  },
  watch: {
    group(newVal) {
      this.localGroup = newVal;
    }
  },
  computed: {
    isEmpty() {
      return (
        this.localGroup.conditions.length === 0 &&
        this.localGroup.childrens.length === 0
      );
    },
    groupOperatorOptions() {
      return [
        { value: "AND", label: this.t('forms-conditions-operator_and') },
        { value: "OR", label: this.t('forms-conditions-operator_or') },
      ];
    },
    questionOptions() {
      return this.availableQuestions.map((question) => ({
        value: question.id,
        label: question.question,
      }));
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    setGroupOperator(value) {
      this.localGroup.conditional_operator = value;
      this.emitChange();
    },
    addCondition() {
      if (!this.localGroup.conditions) {
        this.$set(this.localGroup, 'conditions', []);
      }
      this.localGroup.conditions.push({
        id: `condition_${Date.now()}`,
        question_id: "",
        condition_value: "",
        comparison_operator: ""
      });
      this.emitChange();
    },
    addGroup() {
      if (!this.localGroup.childrens) {
        this.$set(this.localGroup, 'childrens', []);
      }
      this.localGroup.childrens.push({
        conditional_operator: "AND",
        childrens: [],
        conditions: []
      });
      this.emitChange();
    },
    removeCondition(index) {
      this.localGroup.conditions.splice(index, 1);
      this.emitChange();
    },
    removeChildGroup(index) {
      this.localGroup.childrens.splice(index, 1);
      this.emitChange();
    },
    updateChildGroup(index, updatedGroup) {
      this.localGroup.childrens[index] = updatedGroup;
      this.emitChange();
    },
    getQuestionType(questionId) {
      const question = this.availableQuestions.find(q => q.id === questionId);
      return question ? question.type : null;
    },
    getQuestionOptions(questionId) {
      const question = this.availableQuestions.find(q => q.id === questionId);
      if (question && question.options) {
        return question.options.filter(opt => !opt.removed);
      }
      return [];
    },
    requiresNumericInput(operator) {
      return [
        'GREATER_THAN',
        'GREATER_THAN_OR_EQUAL',
        'LESS_THAN',
        'LESS_THAN_OR_EQUAL',
      ].includes(operator);
    },
    range(size, start) {
      return Array.from({ length: size }, (unused, index) => index + start);
    },
    // Empty means the answer is free-form and needs a text or date input.
    valueOptions(condition) {
      switch (this.getQuestionType(condition.question_id)) {
        case 'selectbox':
          return this.getQuestionOptions(condition.question_id).map((option) => ({
            value: option.value !== undefined ? option.value : option.text,
            label: option.text,
          }));
        case 'emotion':
          return this.range(5, 1).map((score) => ({
            value: String(score),
            label: this.t(`forms-conditions-emotion_${score}`),
          }));
        case 'boolean':
          return [
            { value: "yes", label: this.t('forms-conditions-boolean_yes') },
            { value: "no", label: this.t('forms-conditions-boolean_no') },
          ];
        case 'nps':
          return this.range(11, 0).map((score) => ({ value: score, label: String(score) }));
        case 'ces':
          return this.range(7, 1).map((score) => ({ value: score, label: String(score) }));
        default:
          return [];
      }
    },
    // Stored values may be numbers or strings depending on when the condition
    // was created, so match on the string form and hand back the option value.
    matchedValue(condition) {
      const stored = condition.condition_value;
      const match = this.valueOptions(condition).find(
        (option) => String(option.value) === String(stored),
      );
      return match ? match.value : stored;
    },
    setQuestion(condition, value) {
      condition.question_id = value;
      condition.condition_value = "";
      this.emitChange();
    },
    setOperator(condition, value) {
      condition.comparison_operator = value;
      this.emitChange();
    },
    setValue(condition, value) {
      condition.condition_value = value;
      this.emitChange();
    },
    getAvailableOperators(questionId) {
      const questionType = this.getQuestionType(questionId);

      const allOperators = [
        { value: 'EQUAL', label: this.t('forms-conditions-op_equal') },
        { value: 'NOT_EQUAL', label: this.t('forms-conditions-op_not_equal') },
        { value: 'GREATER_THAN', label: this.t('forms-conditions-op_greater_than') },
        { value: 'GREATER_THAN_OR_EQUAL', label: this.t('forms-conditions-op_greater_than_or_equal') },
        { value: 'LESS_THAN', label: this.t('forms-conditions-op_less_than') },
        { value: 'LESS_THAN_OR_EQUAL', label: this.t('forms-conditions-op_less_than_or_equal') },
        { value: 'INCLUDES', label: this.t('forms-conditions-op_includes') },
        { value: 'QUESTION_ANSWERED', label: this.t('forms-conditions-op_answered') }
      ];

      if (!questionType) {
        return allOperators;
      }

      switch(questionType) {
        case 'date':
        case 'emotion':
        case 'nps':
        case 'ces':
          return allOperators.filter(op =>
            ['EQUAL', 'NOT_EQUAL', 'GREATER_THAN', 'GREATER_THAN_OR_EQUAL', 'LESS_THAN', 'LESS_THAN_OR_EQUAL', 'QUESTION_ANSWERED'].includes(op.value)
          );

        case 'selectbox':
          return allOperators.filter(op =>
            ['EQUAL', 'NOT_EQUAL', 'GREATER_THAN', 'GREATER_THAN_OR_EQUAL', 'LESS_THAN', 'LESS_THAN_OR_EQUAL', 'INCLUDES', 'QUESTION_ANSWERED'].includes(op.value)
          );

        case 'comment':
        case 'input':
          return allOperators.filter(op =>
            ['EQUAL', 'NOT_EQUAL', 'INCLUDES', 'QUESTION_ANSWERED'].includes(op.value)
          );

        case 'boolean':
          return allOperators.filter(op =>
            ['EQUAL', 'NOT_EQUAL', 'QUESTION_ANSWERED'].includes(op.value)
          );

        default:
          return allOperators;
      }
    },
    emitChange() {
      this.$emit('change', this.localGroup);
    },
  }
};
</script>

<style lang="scss" scoped>
.condition-group {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  background-color: #fff;

  &.is-nested {
    margin-top: 12px;
    background-color: #f9fafb;
  }
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;

  .operator-selector {
    display: flex;
    align-items: center;
    gap: 8px;

    .operator-label {
      font-size: 13px;
      color: #4d5761;
      white-space: nowrap;
    }

    .operator-select {
      width: 220px;
    }
  }

  .group-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
}

.condition-group.is-compact {
  padding: 14px;

  .group-header {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;

    .operator-selector {
      width: 100%;

      .operator-select {
        width: auto;
        flex: 1;
      }
    }

    .group-actions {
      width: 100%;
    }
  }

  .condition-card-body {
    grid-template-columns: 1fr;
  }
}

.group-body {
  .empty-group {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 28px 16px;
    text-align: center;
    color: #6c737f;
    font-size: 13px;
    background-color: #f9fafb;
    border-radius: 12px;
    border: 1px dashed #e5e7eb;
  }
}

.conditions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.condition-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
}

.condition-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px 6px 14px;
  border-bottom: 1px solid #f3f4f6;

  .condition-label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #9da4ae;
  }
}

.condition-card-body {
  display: grid;
  grid-template-columns: 2fr 1.4fr 1.6fr;
  gap: 12px;
  padding: 14px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
}

.condition-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  .field-label {
    font-size: 12px;
    color: #6c737f;
  }

  ::v-deep .kv-date-picker,
  ::v-deep .kv-date-picker-trigger,
  ::v-deep .kv-input {
    width: 100%;
  }
}

.nested-group-item {
  min-width: 0;
}
</style>
